import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { customerDocumentsApi } from '../services/api';
import {
  Search,
  Upload,
  FileText,
  ChevronDown,
  ChevronRight,
  X,
  File,
  Trash2,
  Eye,
  Clock,
  PenTool,
  Send,
  RotateCcw,
  Ban,
  CheckCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  User,
  Mail,
  Phone,
  Download
} from 'lucide-react';
import toast from 'react-hot-toast';
import CustomerSearch from '../components/CustomerSearch';
import './CustomerDocuments.css';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: '#6b7280', icon: FileText },
  sent: { label: 'Sent', color: '#3b82f6', icon: Send },
  viewed: { label: 'Viewed', color: '#8b5cf6', icon: Eye },
  partially_signed: { label: 'In Progress', color: '#f59e0b', icon: PenTool },
  completed: { label: 'Completed', color: '#16a34a', icon: CheckCircle },
  declined: { label: 'Declined', color: '#dc2626', icon: AlertCircle },
  voided: { label: 'Voided', color: '#9ca3af', icon: Ban },
  expired: { label: 'Expired', color: '#9ca3af', icon: Clock }
};

const EVENT_LABELS = {
  created: 'Document created',
  sent: 'Sent for signing',
  viewed: 'Viewed by recipient',
  field_signed: 'Field signed',
  completed: 'Signing completed',
  declined: 'Declined by recipient',
  voided: 'Voided by admin',
  expired: 'Signing link expired',
  resent: 'Resent to recipient',
  downloaded: 'Downloaded'
};

function CustomerDocuments() {
  const { selectedBlog } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [docEvents, setDocEvents] = useState({});

  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Pre-selected customer for "New Document" from an existing customer
  const [preselectedCustomer, setPreselectedCustomer] = useState(null);

  // Signing link modal
  const [signingLinkDoc, setSigningLinkDoc] = useState(null);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, action, destructive }

  const fetchDocuments = useCallback(async () => {
    if (!selectedBlog) return;
    try {
      setLoading(true);
      const res = await customerDocumentsApi.list(selectedBlog.blog_id, {
        search: search || undefined,
        status: statusFilter || undefined
      });
      setCustomers(res.data.customers || []);
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [selectedBlog, search, statusFilter]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Auto-open upload modal if redirected from WordPress with ?new=1&wp_type=...&wp_id=...
  useEffect(() => {
    if (searchParams.get('new') === '1' && searchParams.get('wp_type') && searchParams.get('wp_id')) {
      const wpType = searchParams.get('wp_type');
      const wpId = searchParams.get('wp_id');
      const originatingContactId = searchParams.get('originating_contact_id');

      // Resolve customer name from WordPress
      if (selectedBlog) {
        import('../services/api').then(({ wordpressApi }) => {
          wordpressApi.getCustomer(selectedBlog.blog_id, wpType, wpId)
            .then(res => {
              const c = res.data.customer;
              openUploadModal({
                name: c.name,
                email: c.email || '',
                phone: c.phone || '',
                wp_type: wpType,
                wp_id: parseInt(wpId),
                originating_contact_id: originatingContactId ? parseInt(originatingContactId) : null
              });
            })
            .catch(() => {
              // Fallback - open modal without pre-filled customer
              openUploadModal();
            });
        });
      }

      // Clear the URL params so refresh doesn't re-trigger
      setSearchParams({}, { replace: true });
    }
  }, [selectedBlog]);

  // Debounced search
  const [searchTimeout, setSearchTimeout] = useState(null);
  const handleSearchChange = (value) => {
    setSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => fetchDocuments(), 300));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
    });
  };

  // Load events for a document
  const loadEvents = async (docId) => {
    if (docEvents[docId]) return;
    try {
      const res = await customerDocumentsApi.getEvents(docId);
      setDocEvents(prev => ({ ...prev, [docId]: res.data.events || [] }));
    } catch {
      // ignore
    }
  };

  // Toggle document detail / events
  const handleToggleDoc = (docId) => {
    if (expandedDoc === docId) {
      setExpandedDoc(null);
    } else {
      setExpandedDoc(docId);
      loadEvents(docId);
    }
  };

  // ── Upload handlers ──
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndSetFile(file);
  };
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) validateAndSetFile(file);
  };

  const validateAndSetFile = (file) => {
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif'];
    if (!allowed.includes(file.type)) { toast.error('Only PDF, PNG, JPG, or GIF files are allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    setUploadFile(file);
    if (!uploadName) setUploadName(file.name.replace(/\.[^/.]+$/, ''));
  };

  const openUploadModal = (customer = null) => {
    setPreselectedCustomer(customer);
    setSelectedCustomer(customer);
    setUploadFile(null);
    setUploadName('');
    setDragOver(false);
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadName('');
    setSelectedCustomer(null);
    setPreselectedCustomer(null);
    setDragOver(false);
  };

  const handleUploadAndEdit = async () => {
    if (!uploadFile || !selectedCustomer) {
      toast.error('Please select a customer and upload a file');
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName || uploadFile.name);
      formData.append('wp_customer_type', selectedCustomer.wp_type);
      formData.append('wp_customer_id', selectedCustomer.wp_id);
      if (selectedCustomer.originating_contact_id) {
        formData.append('originating_contact_id', selectedCustomer.originating_contact_id);
      }
      formData.append('page_count', '1');

      const res = await customerDocumentsApi.create(selectedBlog.blog_id, formData);
      toast.success('Document created');
      handleCloseUploadModal();
      navigate('/customer-document-editor', { state: { customerDocumentId: res.data.id } });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // ── Document actions ──
  const handleSendDoc = async (doc) => {
    try {
      const res = await customerDocumentsApi.send(doc.id);
      toast.success('Document sent for signing');
      setSigningLinkDoc({ ...doc, signing_url: res.data.signing_url });
      fetchDocuments();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to send'); }
  };

  const handleResendDoc = async (doc) => {
    try {
      const res = await customerDocumentsApi.resend(doc.id);
      toast.success('Document resent');
      setSigningLinkDoc({ ...doc, signing_url: res.data.signing_url });
      fetchDocuments();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to resend'); }
  };

  const handleVoidDoc = (doc) => {
    setConfirmModal({
      title: 'Void Document',
      message: `Are you sure you want to void "${doc.name}"? The signing link will be invalidated and the recipient will no longer be able to sign.`,
      destructive: true,
      confirmLabel: 'Void Document',
      action: async () => {
        try {
          await customerDocumentsApi.void(doc.id);
          toast.success('Document voided');
          fetchDocuments();
        } catch (err) { toast.error(err.response?.data?.error || 'Failed to void'); }
      }
    });
  };

  const handleDeleteDoc = (doc) => {
    setConfirmModal({
      title: 'Delete Document',
      message: `Are you sure you want to delete "${doc.name}"? This action cannot be undone and all associated data will be permanently removed.`,
      destructive: true,
      confirmLabel: 'Delete',
      action: async () => {
        try {
          await customerDocumentsApi.delete(doc.id);
          toast.success('Document deleted');
          fetchDocuments();
        } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete'); }
      }
    });
  };

  const handleDownloadDoc = (doc) => {
    const token = localStorage.getItem('token');
    const url = customerDocumentsApi.downloadUrl(doc.id);
    // Open in new tab with auth - the browser will download
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    // Fetch with auth and trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${doc.name} - Signed.pdf`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error('Failed to download'));
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Signing link copied');
  };

  const handleViewDoc = (doc) => {
    navigate('/customer-document-editor', { state: { customerDocumentId: doc.id } });
  };

  if (!selectedBlog) {
    return <div className="customer-docs-empty"><p>Please select a blog to view customer documents.</p></div>;
  }

  return (
    <div className="customer-docs">
      <div className="customer-docs-header">
        <h1>Customer Documents</h1>
        <button className="btn btn-primary" onClick={() => openUploadModal()}>
          <Upload size={16} /> New Document
        </button>
      </div>

      <p className="customer-docs-subtitle">
        Manage documents for customers. Select a customer to view their documents or send a new one.
      </p>

      <div className="customer-docs-filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by customer name, email, or document..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {search && (
            <button className="search-clear" onClick={() => { setSearch(''); }}>
              <X size={16} />
            </button>
          )}
        </div>
        <select className="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="customer-docs-empty"><div className="spinner" /><p>Loading...</p></div>
      ) : customers.length === 0 ? (
        <div className="customer-docs-empty">
          <User size={48} />
          <h2>No documents found</h2>
          <p>{search || statusFilter ? 'Try adjusting your filters' : 'Upload a document to get started'}</p>
        </div>
      ) : (
        <div className="cd-customer-list">
          {customers.map(customer => {
            const key = `${customer.wp_customer_type}_${customer.wp_customer_id}`;
            const isExpanded = expandedCustomer === key;
            const docs = customer.documents || [];
            const completedCount = docs.filter(d => d.status === 'completed').length;
            const pendingCount = docs.filter(d => ['sent', 'viewed', 'partially_signed'].includes(d.status)).length;

            return (
              <div key={key} className={`cd-customer-card ${isExpanded ? 'expanded' : ''}`}>
                {/* Customer header */}
                <div className="cd-customer-header" onClick={() => setExpandedCustomer(isExpanded ? null : key)}>
                  <div className="cd-customer-avatar">
                    {customer.customer_name.split(' ').map(n => n[0]).filter(Boolean).join('').substring(0, 2).toUpperCase()}
                  </div>
                  <div className="cd-customer-info">
                    <span className="cd-customer-name">{customer.customer_name}</span>
                    <div className="cd-customer-meta">
                      {customer.customer_email && <span><Mail size={12} /> {customer.customer_email}</span>}
                    </div>
                  </div>
                  <div className="cd-customer-stats">
                    <span className="cd-stat">{docs.length} doc{docs.length !== 1 ? 's' : ''}</span>
                    {completedCount > 0 && <span className="cd-stat completed">{completedCount} signed</span>}
                    {pendingCount > 0 && <span className="cd-stat pending">{pendingCount} pending</span>}
                  </div>
                  <div className="cd-customer-actions" onClick={e => e.stopPropagation()}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => openUploadModal({
                        name: customer.customer_name,
                        email: customer.customer_email,
                        phone: '',
                        wp_type: customer.wp_customer_type,
                        wp_id: customer.wp_customer_id
                      })}
                    >
                      <Upload size={14} /> New
                    </button>
                  </div>
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>

                {/* Documents list */}
                {isExpanded && (
                  <div className="cd-docs-list">
                    {docs.map(doc => {
                      const statusCfg = STATUS_CONFIG[doc.status] || STATUS_CONFIG.draft;
                      const StatusIcon = statusCfg.icon;
                      const stats = doc.field_stats || {};
                      const isDocExpanded = expandedDoc === doc.id;
                      const events = docEvents[doc.id] || [];

                      return (
                        <div key={doc.id} className="cd-doc-item">
                          <div className="cd-doc-row" onClick={() => handleToggleDoc(doc.id)}>
                            <div className="cd-doc-icon"><File size={16} /></div>
                            <div className="cd-doc-info">
                              <span className="cd-doc-name">{doc.name}</span>
                              <span className="cd-doc-file">{doc.file_original_name}</span>
                            </div>
                            <span className="cd-doc-status" style={{ color: statusCfg.color, background: `${statusCfg.color}12` }}>
                              <StatusIcon size={12} /> {statusCfg.label}
                            </span>
                            <span className="cd-doc-date">{formatDate(doc.sent_at || doc.created_at)}</span>
                            <div className="cd-doc-actions" onClick={e => e.stopPropagation()}>
                              {doc.status === 'draft' && (
                                <>
                                  <button className="btn-icon-sm" title="Edit" onClick={() => handleViewDoc(doc)}><PenTool size={13} /></button>
                                  <button className="btn-icon-sm primary" title="Send" onClick={() => handleSendDoc(doc)}><Send size={13} /></button>
                                </>
                              )}
                              {['sent', 'viewed', 'partially_signed'].includes(doc.status) && (
                                <>
                                  <button className="btn-icon-sm" title="Resend" onClick={() => handleResendDoc(doc)}><RotateCcw size={13} /></button>
                                  <button className="btn-icon-sm" title="Void" onClick={() => handleVoidDoc(doc)}><Ban size={13} /></button>
                                </>
                              )}
                              {doc.status === 'completed' && (
                                <>
                                  <button className="btn-icon-sm" title="View" onClick={() => handleViewDoc(doc)}><Eye size={13} /></button>
                                  <button className="btn-icon-sm" title="Download" onClick={() => handleDownloadDoc(doc)}><Download size={13} /></button>
                                </>
                              )}
                              <button className="btn-icon-sm danger" title="Delete" onClick={() => handleDeleteDoc(doc)}><Trash2 size={13} /></button>
                            </div>
                          </div>

                          {/* Expanded: details + activity log */}
                          {isDocExpanded && (
                            <div className="cd-doc-detail">
                              <div className="cd-doc-detail-grid">
                                {doc.created_at && <div className="cd-detail"><label>Created</label><span>{formatDate(doc.created_at)}</span></div>}
                                {doc.sent_at && <div className="cd-detail"><label>Sent</label><span>{formatDate(doc.sent_at)}</span></div>}
                                {doc.viewed_at && <div className="cd-detail"><label>First Viewed</label><span>{formatDate(doc.viewed_at)}</span></div>}
                                {doc.completed_at && <div className="cd-detail"><label>Completed</label><span>{formatDate(doc.completed_at)}</span></div>}
                                {doc.expires_at && <div className="cd-detail"><label>Expires</label><span>{formatDate(doc.expires_at)}</span></div>}
                                {stats.total_fields > 0 && (
                                  <div className="cd-detail"><label>Fields</label><span>{stats.required_signed || 0} of {stats.required_fields || 0} signed</span></div>
                                )}
                              </div>

                              {events.length > 0 && (
                                <div className="cd-activity-log">
                                  <h4>Activity</h4>
                                  {events.map(evt => {
                                    let label = EVENT_LABELS[evt.event_type] || evt.event_type;
                                    // Show field details for field_signed events
                                    if (evt.event_type === 'field_signed') {
                                      try {
                                        const data = JSON.parse(evt.event_data);
                                        if (data.label) label = `${data.label} signed`;
                                      } catch {}
                                    }
                                    return (
                                      <div key={evt.id} className="cd-event-row">
                                        <div className={`cd-event-dot ${evt.event_type === 'completed' ? 'completed' : ''}`} />
                                        <div className="cd-event-content">
                                          <span className="cd-event-label">{label}</span>
                                          <span className="cd-event-time">{formatDate(evt.created_at)}</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={handleCloseUploadModal}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h2>New Customer Document</h2>
              <button className="btn-close" onClick={handleCloseUploadModal}><X size={20} /></button>
            </div>
            <div className="upload-modal-body">
              {/* Customer search - skip if preselected */}
              <div className="upload-form-group">
                <label>Customer *</label>
                {preselectedCustomer ? (
                  <div className="selected-customer-badge">
                    <span className="selected-customer-name">{preselectedCustomer.name}</span>
                    {preselectedCustomer.email && <span className="selected-customer-detail">{preselectedCustomer.email}</span>}
                  </div>
                ) : (
                  <>
                    <CustomerSearch
                      blogId={selectedBlog?.blog_id}
                      onSelect={(customer) => setSelectedCustomer(customer)}
                    />
                    {selectedCustomer && (
                      <div className="selected-customer-badge">
                        <span className="selected-customer-name">{selectedCustomer.name}</span>
                        {selectedCustomer.email && <span className="selected-customer-detail">{selectedCustomer.email}</span>}
                        <button className="selected-customer-clear" onClick={() => setSelectedCustomer(null)}><X size={14} /></button>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="upload-form-group">
                <label>Document Name</label>
                <input type="text" value={uploadName} onChange={(e) => setUploadName(e.target.value)} placeholder="e.g., Pre-Need Agreement" />
              </div>

              {!uploadFile ? (
                <div className={`upload-dropzone ${dragOver ? 'drag-over' : ''}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                  <Upload size={40} />
                  <p className="upload-dropzone-text">Drag & drop your document here</p>
                  <p className="upload-dropzone-subtext">PDF, PNG, JPG, or GIF - up to 10MB</p>
                  <label className="btn btn-secondary upload-browse-btn">
                    Browse Files
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg,.gif" onChange={handleFileSelect} hidden />
                  </label>
                </div>
              ) : (
                <div className="upload-file-preview">
                  <div className="upload-file-icon"><File size={32} /></div>
                  <div className="upload-file-info">
                    <span className="upload-file-name">{uploadFile.name}</span>
                    <span className="upload-file-size">{(uploadFile.size / 1024).toFixed(1)} KB</span>
                  </div>
                  <button className="btn btn-icon btn-danger" onClick={() => setUploadFile(null)} title="Remove"><Trash2 size={16} /></button>
                </div>
              )}
            </div>
            <div className="upload-modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseUploadModal}>Cancel</button>
              <button className="btn btn-primary" disabled={!uploadFile || !selectedCustomer || uploading} onClick={handleUploadAndEdit}>
                {uploading ? 'Uploading...' : <><PenTool size={16} /> Upload & Place Fields</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signing Link Modal */}
      {signingLinkDoc && (
        <div className="upload-modal-overlay" onClick={() => setSigningLinkDoc(null)}>
          <div className="upload-modal signing-link-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h2>Document Sent</h2>
              <button className="btn-close" onClick={() => setSigningLinkDoc(null)}><X size={20} /></button>
            </div>
            <div className="upload-modal-body">
              <div className="signing-link-success">
                <CheckCircle size={32} />
                <p>Document sent to <strong>{signingLinkDoc.customer_email}</strong></p>
              </div>
              <div className="signing-link-box">
                <label>Signing Link</label>
                <div className="signing-link-input">
                  <input type="text" readOnly value={signingLinkDoc.signing_url} />
                  <button className="btn btn-icon" onClick={() => handleCopyLink(signingLinkDoc.signing_url)}><Copy size={14} /></button>
                  <a href={signingLinkDoc.signing_url} target="_blank" rel="noopener noreferrer" className="btn btn-icon"><ExternalLink size={14} /></a>
                </div>
              </div>
            </div>
            <div className="upload-modal-footer">
              <button className="btn btn-primary" onClick={() => setSigningLinkDoc(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="upload-modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="upload-modal confirm-action-modal" onClick={e => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h2>{confirmModal.title}</h2>
              <button className="btn-close" onClick={() => setConfirmModal(null)}><X size={20} /></button>
            </div>
            <div className="upload-modal-body">
              <p className="confirm-action-message">{confirmModal.message}</p>
            </div>
            <div className="upload-modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmModal(null)}>Cancel</button>
              <button
                className={`btn ${confirmModal.destructive ? 'btn-danger' : 'btn-primary'}`}
                onClick={async () => {
                  await confirmModal.action();
                  setConfirmModal(null);
                }}
              >
                {confirmModal.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerDocuments;
